import { 
  Family, Parent, Student, Class, 
  BillingStatus, InviteStatus, Payment
} from '@/types/models';
import { mapToBillingStatus, mapToInviteStatus } from './typeUtils';

/**
 * Type mappers for converting Supabase responses to typed model objects
 */

export const mapToFamily = (data: any): Family => {
  if (!data) throw new Error("Cannot map null data to Family");
  
  return {
    id: data.id,
    organization_id: data.organization_id,
    name: data.name,
    billing_status: mapToBillingStatus(data.billing_status as string),
    created_at: data.created_at
  };
};

export const mapToParent = (data: any): Parent => {
  if (!data) throw new Error("Cannot map null data to Parent");
  
  return {
    id: data.id,
    family_id: data.family_id,
    organization_id: data.organization_id,
    user_id: data.user_id,
    first_name: data.first_name,
    last_name: data.last_name,
    email: data.email || '',
    phone: data.phone || '',
    invite_status: mapToInviteStatus(data.invite_status as string),
    is_primary: data.is_primary || false,
    created_at: data.created_at,
    // Address fields
    address_street: data.address_street || undefined,
    address_city: data.address_city || undefined,
    address_state: data.address_state || undefined,
    address_zip: data.address_zip || undefined,
    address_country: data.address_country || 'United States',
    // Status fields
    is_active: data.is_active !== false, // Default to true if not defined
    is_deleted: data.is_deleted || false,
    deleted_at: data.deleted_at || undefined
  };
};

export const mapToStudent = (data: any): Student => {
  if (!data) throw new Error("Cannot map null data to Student");
  
  return {
    id: data.id,
    family_id: data.family_id,
    organization_id: data.organization_id,
    first_name: data.first_name,
    last_name: data.last_name,
    preferred_name: data.preferred_name,
    date_of_birth: data.date_of_birth,
    gender: data.gender,
    notes: data.notes,
    created_at: data.created_at,
    
    // Contact information
    address_line1: data.address_line1,
    address_line2: data.address_line2,
    city: data.city,
    state: data.state,
    zip: data.zip,
    phone: data.phone,
    email: data.email,
    
    // Additional info
    location_id: data.location_id,
    profile_picture_url: data.profile_picture_url,
    start_date: data.start_date,
    drop_date: data.drop_date,
    is_active: data.is_active,
    no_public_photos: data.no_public_photos
  };
};

export const mapToClass = (data: any): Class => {
  if (!data) throw new Error("Cannot map null data to Class");
  
  return {
    id: data.id,
    organization_id: data.organization_id,
    name: data.name,
    description: data.description || '',
    max_students: data.max_students,
    location: data.location,
    dress_code: data.dress_code,
    level: data.level,
    price_cents: data.price_cents,
    season_id: data.season_id,
    is_active: data.is_active,
    is_event: data.is_event,
    event_date: data.event_date,
    event_start_time: data.event_start_time,
    event_end_time: data.event_end_time,
    created_at: data.created_at
  };
};

export const mapToPayment = (data: any): Payment => {
  if (!data) throw new Error("Cannot map null data to Payment");
  
  return {
    id: data.id,
    family_id: data.family_id,
    organization_id: data.organization_id,
    amount_cents: data.amount_cents,
    paid_at: data.paid_at,
    stripe_payment_intent: data.stripe_payment_intent,
    status: data.status,
    receipt_url: data.receipt_url,
    created_at: data.created_at
  };
};

// Helper to map arrays of items
export const mapArrayToType = <T>(
  array: any[], 
  mapper: (item: any) => T
): T[] => {
  if (!array) return [];
  return array.map(item => mapper(item));
};
